import React, { useState, useCallback, useContext } from 'react'
import { Field } from 'react-final-form'
import styled from 'styled-components'
import { generatePath } from 'react-router'
import { mapValues, groupBy, get } from 'lodash'

import { VARIANT_WARNING, VARIANT_SECONDARY } from '../constants/variants'
import { ROLE_OPTIONS, BOOL_OPTIONS } from '../constants/roles'
import { changeEmailPath, changeEmail } from '../requests/changeEmail'
import { useRequest } from '../hooks/request'
import { getFullName } from '../utils/getFullName'
import { dateToDateTime } from '../utils/dateToDDMMYYYY'
import { PATHS } from '../constants/paths'
import { DIPLOMAS_OPTIONS, DIPLOMAS } from '../constants/diplomas'
import { UnitsContext } from '../data/UnitsContext'

import { Form } from './common/Form'
import { Button } from './common/Button'
import { InputFieldWidth } from './fields/InputField'
import { Menu, H3, Body } from './common/Typography'
import { SelectFieldWidth } from './fields/SelectField'
import { Margin } from './common/Margin'
import { HoverLink } from './common/HoverLink'
import { Trad } from './common/Trad'
import { Block } from './common/Block'
import { ConfirmModal } from './common/ConfirmModal'


export const UserForm = ({
  disabledFields = {}, confirmLabel, initialValues, onSubmit, path, initOpen = false,
  successMessage, showChangeEmail, roleOptions = ROLE_OPTIONS, boolOptions = BOOL_OPTIONS,
}) => {
  const [changing, setChanging] = useState(false)
  const { units } = useContext(UnitsContext)

  const changeEmailRequest = useRequest({
    request: changeEmail,
  })
  const [isOpen, setIsOpen] = useState(initOpen)
  const [disabledUnit, setDisabledUnit] = useState(!!initialValues)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const finalOnConfirm = () => {
    setDisabledUnit(false)
    close()
  }

  return (
    <Form path={path} onSubmit={onSubmit} initialValues={initialValues}>
      {({ handleSubmit, form }) => {
        const state = form.getState()

        return (
          <>
            <Section title="genericInformations">
              <Field
                disabled={disabledFields.lastName}
                required
                component={InputFieldWidth}
                name="lastName"
                label="lastname"
                placeholder="lastnamePlaceholder"
              />
              <Field
                disabled={disabledFields.firstName}
                required
                component={InputFieldWidth}
                name="firstName"
                label="firstname"
                placeholder="firstnamePlaceholder"
              />
            </Section>
            <Section title="accountInformations">
              <Field
                disabled={disabledFields.email}
                required
                component={InputFieldWidth}
                name="email"
                label="email"
                placeholder="emailPlaceholder"
              />
              <Field
                disabled={disabledFields.phone}
                required
                component={InputFieldWidth}
                name="phone"
                label="phone"
                placeholder="phonePlaceholder"
              />
            </Section>
            <div>
              {showChangeEmail && (
                !changing ? (
                  <Button
                    tradId="changeEmail"
                    variant={VARIANT_SECONDARY}
                    onClick={() => setChanging(true)}
                  />
                )
                  : changeEmailRequest.state.success ? <Menu tradId="emailSent" /> : (
                    <Form path={changeEmailPath} onSubmit={changeEmailRequest.fetch}>
                      {(props) => (
                        <ChangeEmail>
                          <Field required component={InputFieldWidth} name="email" label="newEmail" />
                          <div>
                            <Button
                              tradId="sendConfirmation"
                              onClick={props.handleSubmit}
                              variant={VARIANT_SECONDARY}
                            />
                            <Margin h={10} />
                            <Button tradId="cancel" variant={VARIANT_WARNING} onClick={() => setChanging(false)} />
                          </div>
                        </ChangeEmail>
                      )}

                    </Form>
                  )
              )}
            </div>
            <Margin v={30} />
            <Section title="accountType">
              <Field
                disabled={disabledFields.role}
                required
                component={SelectFieldWidth}
                translate
                options={roleOptions}
                name="role"
                label="role"
                placeholder="rolePlaceholder"
              />
              <Field
                disabled={disabledFields.role}
                required
                component={SelectFieldWidth}
                translate
                options={boolOptions}
                name="isInternshipDirector"
                label="isInternshipDirector"
              />
              <Field
                disabled={disabledFields.role}
                required
                component={SelectFieldWidth}
                translate
                options={boolOptions}
                name="isEvaluator"
                label="isEvaluator"
              />
              <HorizontalDiv>
                <Field
                  disabled={disabledUnit}
                  required
                  component={SelectFieldWidth}
                  name="unit"
                  label="unit"
                  placeholder="unitPlaceholder"
                  options={units}
                />
                <Button
                  onClick={open}
                  tradId="modifyUnit"
                  variant={VARIANT_SECONDARY}
                  hidden={!disabledUnit}
                />
              </HorizontalDiv>
            </Section>
            <Section title="otherInformations">
              <Field
                disabled={disabledFields.diplomas}
                required
                component={SelectFieldWidth}
                name="diplomas"
                label="diploma"
                placeholder="diplomaPlaceholder"
                options={DIPLOMAS_OPTIONS}
                translate
              />
              {state.values.diplomas === DIPLOMAS.OTHER && (
                <Field
                  required
                  component={InputFieldWidth}
                  name="diplomasOtherDetail"
                  label="diplomasOtherDetail"
                />
              )}
            </Section>
            <div>
              <Button
                onClick={handleSubmit}
                loading={state.submitting}
                tradId={confirmLabel}
                variant={VARIANT_SECONDARY}
              />
              <Margin h={10} />
              {isOpen && (
                <ConfirmModal
                  titleTrad="unitUpdateConfirm"
                  onConfirm={finalOnConfirm}
                  confirmDisabled={false}
                  onCloseRequest={close}
                />
              )}
              {state.submitSucceeded && !state.dirtySinceLastSubmit && <Body tradId={successMessage} />}
            </div>
            {(get(initialValues, 'courses.length') > 0
              && (
                <div>
                  {initialValues.courses.map((c) => {
                    const evaluationsByTrainee = mapValues(groupBy(c.evaluations, 'traineeId'), (t) => t[0])

                    return (
                      <div key={c.id}>
                        <div>
                          <HoverLink to={generatePath(PATHS.COURSE_EDIT, { id: c.id })}>
                            <H3 block underlined>
                              {c.internship.name}
                              {' '}
                              -
                              {' '}
                              {c.name}
                            </H3>
                          </HoverLink>
                          <Margin v={10} />
                        </div>
                        {c.trainees.map((t) => (
                          <div key={t.id}>
                            <HoverLink to={generatePath(PATHS.TRAINEE_SHOW, { id: t.id })}>
                              <Menu>{getFullName({ user: t })}</Menu>
                            </HoverLink>
                            {' - '}
                            {evaluationsByTrainee[t.id]
                              ? (
                                <HoverLink
                                  to={generatePath(PATHS.EVALUATION_SHOW, { id: evaluationsByTrainee[t.id].id })}
                                >
                                  <Menu
                                    tradId="evaluatedOn"
                                    values={{ on: dateToDateTime(evaluationsByTrainee[t.id].publicationDate) }}
                                  />
                                </HoverLink>
                              )
                              : <Menu tradId="notEvaluatedYet" />}
                            <Margin v={7} />
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )
            )}
          </>
        )
      }}
    </Form>
  )
}

const Section = ({ title, children }) => (
  <>
    <H3 variant={VARIANT_SECONDARY} block bold>
      <Trad id={title} />
    </H3>
    <Margin v={15} />
    <VerticalBlock border>
      {children}
    </VerticalBlock>
    <Margin v={30} />
  </>
)

const ChangeEmail = styled.div`
`

const VerticalBlock = styled(Block)`
  display: flex;
  flex-direction: column;
`

const HorizontalDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
